// Modules
import moment from "moment";

// Interfaces
import { Transaction } from 'Interfaces/Transactions';

enum TransactionsSortType {
  ID = "id",
  DATE = "date",
  TYPE = "type",
  CONTRACT = "contract",
  USAGE_YEAR = "usage_year",
  USAGE_TYPE = "usage_type",
  AMOUNT = "amount",
};

const SORT_FIELDS = {
  [TransactionsSortType.ID]: "id",
  [TransactionsSortType.DATE]: "date",
  [TransactionsSortType.TYPE]: "friendlyTransactionType",
  [TransactionsSortType.CONTRACT]: "contractId",
  [TransactionsSortType.USAGE_YEAR]: "usageYear",
  [TransactionsSortType.USAGE_TYPE]: "friendlyUsageType",
  [TransactionsSortType.AMOUNT]: "amount",
}

enum SortDirection {
  ASC = 'ascending',
  DESC = 'descending',
}

/**
 * Evaluate a sort for two transactions based on id
 */
 function evalIdSort(idA: number, idB: number, sortDirection: SortDirection) {
  
  let compare = idA - idB;
  if (sortDirection === SortDirection.DESC)
    compare = idB - idA;

  // Sort by ID was successful
  if (compare > 0) {
    return 1;
  } else if (compare < 0) {
    return -1;
  }

  // IDs are identical
  return 0;
}

/**
 * Evaluate a sort for two transactions based on date
 */
function evalDateSort(dateA: string, dateB: string, sortDirection: SortDirection) {
  const firstDateTime = dateA.split('T')[1].split(':');
  const secondDateTime = dateB.split('T')[1].split(':');

  const firstDate = moment(dateA.split('T')[0]).set('hour', parseInt(firstDateTime[0])).set('minute', parseInt(firstDateTime[1])).set('second', parseInt(firstDateTime[2]));
  const secondDate = moment(dateB.split('T')[0]).set('hour', parseInt(secondDateTime[0])).set('minute', parseInt(secondDateTime[1])).set('second', parseInt(secondDateTime[2]));

  let dateCompare = firstDate.valueOf() - secondDate.valueOf();
  if (sortDirection === SortDirection.DESC)
    dateCompare = secondDate.valueOf() - firstDate.valueOf();

  // Date sort was successful
  if (dateCompare > 0) {
    return 1;
  } else if (dateCompare < 0){
    return -1;
  }

  // Dates are identical
  return 0;
}

/**
 * Evaluate a sort for two transactions based on type
 */
 function evalTypeSort(typeA: string, typeB: string, sortDirection: SortDirection) {
  let letterCompare = (typeA).localeCompare(typeB);
  if (sortDirection === SortDirection.DESC)
    letterCompare = (typeB).localeCompare(typeA);

  // Sort by letters in Type was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Types are identical
  return 0;
}

/**
 * Evaluate a sort for two transactions based on contract id
 */
 function evalContractSort(contractA: string, contractB: string, sortDirection: SortDirection) {
  let letterCompare = (contractA).localeCompare(contractB);
  if (sortDirection === SortDirection.DESC)
    letterCompare = (contractB).localeCompare(contractA);

  // Sort by letters in Contract was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Contracts are identical
  return 0;
}

/**
 * Evaluate a sort for two transactions based on usage year
 */
 function evalUsageYearSort(yearA: number, yearB: number, sortDirection: SortDirection) {
  
  let compare = yearA - yearB;
  if (sortDirection === SortDirection.DESC)
    compare = yearB - yearA;

  // Sort by Usage Year was successful
  if (compare > 0) {
    return 1;
  } else if (compare < 0) {
    return -1;
  }

  // Usage Years are identical
  return 0;
}

/**
 * Evaluate a sort for two transactions based on usage type
 */
 function evalUsageTypeSort(usageTypeA: string, usageTypeB: string, sortDirection: SortDirection) {
  let letterCompare = (usageTypeA).localeCompare(usageTypeB);
  if (sortDirection === SortDirection.DESC)
    letterCompare = (usageTypeB).localeCompare(usageTypeA);

  // Sort by letters in Usage Type was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Usage Types are identical
  return 0;
}

/**
 * Evaluate a sort for two transactions based on amount
 */
 function evalAmountSort(amountA: number, amountB: number, sortDirection: SortDirection) {
  
  let compare = amountA - amountB;
  if (sortDirection === SortDirection.DESC)
    compare = amountB - amountA;

  // Sort by Amount was successful
  if (compare > 0) {
    return 1;
  } else if (compare < 0) {
    return -1;
  }

  // Amounts are identical
  return 0;
}

/**
 * Sort function based on Id
 */
 function idSortFunc(a: any, b:any, sortDirection: SortDirection) {
  // Try to sort by Id
  const sortResult = evalIdSort(
    a[SORT_FIELDS[TransactionsSortType.ID]],
    b[SORT_FIELDS[TransactionsSortType.ID]],
    sortDirection
  );

  // Id sort was successful
  if (sortResult > 0) {
    return 1;
  } else if (sortResult < 0) {
    return -1;
  }

  // Ids are identical
  return 0;
}

/**
 * Sort function based on Date (Check in date)
 */
 function dateSortFunc(a: any, b:any, sortDirection: SortDirection) {
  // Try to sort by Date
  const sortResult = evalDateSort(
    a[SORT_FIELDS[TransactionsSortType.DATE]],
    b[SORT_FIELDS[TransactionsSortType.DATE]],
    sortDirection
  );

  // Date sort was successful
  if (sortResult > 0) {
    return 1;
  } else if (sortResult < 0) {
    return -1;
  }

  // Dates are identical
  return 0;
}

/**
 * Sort function based on Type
 */
 function typeSortFunc(a: any, b:any, sortDirection: SortDirection) {
  // Try to sort by Type
  const sortResult = evalTypeSort(
    a[SORT_FIELDS[TransactionsSortType.TYPE]],
    b[SORT_FIELDS[TransactionsSortType.TYPE]],
    sortDirection
  );

  // Type sort was successful
  if (sortResult > 0) {
    return 1;
  } else if (sortResult < 0) {
    return -1;
  }

  // Types are identical
  return 0;
}

/**
 * Sort function based on Contract
 */
 function contractSortFunc(a: any, b:any, sortDirection: SortDirection) {
  // Try to sort by Contract
  const sortResult = evalContractSort(
    a[SORT_FIELDS[TransactionsSortType.CONTRACT]],
    b[SORT_FIELDS[TransactionsSortType.CONTRACT]],
    sortDirection
  );

  // Contract sort was successful
  if (sortResult > 0) {
    return 1;
  } else if (sortResult < 0) {
    return -1;
  }

  // Contracts are identical
  return 0;
}

/**
 * Sort function based on Usage Year
 */
 function usageYearSortFunc(a: any, b:any, sortDirection: SortDirection) {
  // Try to sort by Usage Year
  const sortResult = evalUsageYearSort(
    a[SORT_FIELDS[TransactionsSortType.USAGE_YEAR]],
    b[SORT_FIELDS[TransactionsSortType.USAGE_YEAR]],
    sortDirection
  );

  // Usage Year sort was successful
  if (sortResult > 0) {
    return 1;
  } else if (sortResult < 0) {
    return -1;
  }

  // Usage Years are identical
  return 0;
}

/**
 * Sort function based on Usage Type
 */
 function usageTypeSortFunc(a: any, b:any, sortDirection: SortDirection) {
  // Try to sort by Usage Type
  const sortResult = evalUsageTypeSort(
    a[SORT_FIELDS[TransactionsSortType.USAGE_TYPE]],
    b[SORT_FIELDS[TransactionsSortType.USAGE_TYPE]],
    sortDirection
  );

  // Usage Type sort was successful
  if (sortResult > 0) {
    return 1;
  } else if (sortResult < 0) {
    return -1;
  }

  // Usage Types are identical
  return 0;
}

/**
 * Sort function based on Amount
 */
 function amountSortFunc(a: any, b:any, sortDirection: SortDirection) {
  // Try to sort by Amount
  const sortResult = evalAmountSort(
    a[SORT_FIELDS[TransactionsSortType.AMOUNT]],
    b[SORT_FIELDS[TransactionsSortType.AMOUNT]],
    sortDirection
  );

  // Amount sort was successful
  if (sortResult > 0) {
    return 1;
  } else if (sortResult < 0) {
    return -1;
  }

  // Amounts are identical
  return 0;
}

/**
 * sort Transactions
 *
 * This is fired when the transactions are first loaded, and when a column sort
 * icon is clicked.
 *
 * If a sort icon was clicked, the switch logic is used for the sort field
 * that was clicked. If it's the initial load, sortField and
 * sortDirection will not be passed as params, and the default case will be
 * used in the switch.
 */
function sortTransactions(
  transactions: Transaction[],
  sortField?: TransactionsSortType,
  sortDirection?: SortDirection
): Transaction[] {

  let sortFunction:any
  switch (sortField) {
    // Sort by Id
    case TransactionsSortType.ID:
      sortFunction = (a:any, b:any) => idSortFunc(a, b, sortDirection ? sortDirection : SortDirection.ASC);
      break;

    // Sort by Date
    case TransactionsSortType.DATE:
      sortFunction = (a:any, b:any) => dateSortFunc(a, b, sortDirection ? sortDirection : SortDirection.DESC);
      break;

    // Sort by Type
    case TransactionsSortType.TYPE:
      sortFunction = (a:any, b:any) => typeSortFunc(a, b, sortDirection ? sortDirection : SortDirection.ASC);
      break;

    // Sort by Contract
    case TransactionsSortType.CONTRACT:
      sortFunction = (a:any, b:any) => contractSortFunc(a, b, sortDirection ? sortDirection : SortDirection.ASC);
      break;

    // Sort by Usage Year
    case TransactionsSortType.USAGE_YEAR:
      sortFunction = (a:any, b:any) => usageYearSortFunc(a, b, sortDirection ? sortDirection : SortDirection.ASC);
      break;

    // Sort by Usage Type
    case TransactionsSortType.USAGE_TYPE:
      sortFunction = (a:any, b:any) => usageTypeSortFunc(a, b, sortDirection ? sortDirection : SortDirection.ASC);
      break;

    // Sort by Amount
    case TransactionsSortType.AMOUNT:
      sortFunction = (a:any, b:any) => amountSortFunc(a, b, sortDirection ? sortDirection : SortDirection.ASC);
      break;

    // default sort
    default:
      sortFunction = (a:any, b:any) => dateSortFunc(a, b, sortDirection ? sortDirection : SortDirection.DESC);
  }

  // Return the sorted contracts
  return [...transactions].sort(sortFunction);
}

export {
  sortTransactions,
  TransactionsSortType,
  SortDirection,
};
